import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { store } from "./store";
import { EnrichmentPendingChange } from "./flowSlice";

interface ExportJobState {
  tableId: string;
  storeName: string;
  enrichmentJobId: string;
  enrichmentDbId: string;
  userEmail: string;
  collectionName: string;
  updateJobId?: string;
  exportJobId?: string;
  syncJobId?: string;
  currentSubTask: 0 | 1 | 2; //update, export, sync
  status: "idle" | "running" | "completed" | "failed";
  startedAt: number;
  progress: number;
  error?: {
    failReason: string;
    failTimestamp: number;
  }
  exportedFields?: string[];
  updatedFields?: {
    source_id?: string | undefined;
    product_id: string;
    field_name: string;
    field_value: string;
}[];
}

interface GenericJobState{
  identifier: string;
  trackingId: string;
  status: "idle" | "running" | "completed" | "failed";
  progress: number;
  payload?: any;
  failReason?: string;
  startedAt?: number;
  finishedAt?: number;
}

export interface JobsState {
  activeExportJobs: Record<string, ExportJobState>;
  activeGenericJobs: Record<string, GenericJobState>;
}

const initialState: JobsState = {
  activeExportJobs: {},
  activeGenericJobs: {},
};

export const jobsSlice = createSlice({
  name: "jobs",
  initialState: initialState,
  reducers: {
    startGenericJob: (state, action: PayloadAction<{identifier: string; trackingId: string}>) => {
      state.activeGenericJobs[action.payload.identifier] = {
        identifier: action.payload.identifier,
        trackingId: action.payload.trackingId,
        status: "running",
        progress: 0,
        startedAt: Date.now(),
      }
    },
    completeGenericJob: (state: JobsState, action: PayloadAction<{
      identifier: string;
      payload?: any;
    }>) => {
      const job = state.activeGenericJobs[action.payload.identifier];
      if (job) {
        job.status = "completed";
        job.payload = action.payload.payload;
        job.finishedAt = Date.now();
      }
    },
    failGenericJob: (state: JobsState, action: PayloadAction<{
      identifier: string;
      reason: string;
    }>) => {
      const job = state.activeGenericJobs[action.payload.identifier];
      if (job) {
        job.status = "failed";
        job.failReason = action.payload.reason;
        job.finishedAt = Date.now();
      }
    },
    deleteGenericJob: (state: JobsState, action: PayloadAction<string>) => {
      delete state.activeGenericJobs[action.payload];
    },
    updateGenericJobProgress: (state: JobsState, action: PayloadAction<{identifier: string; progress: number}>) => {
      const job = state.activeGenericJobs[action.payload.identifier];
      if (job) {
        job.progress = action.payload.progress;
      }
    },
    removeAllGenericJobs: (state: JobsState) => {
      state.activeGenericJobs = {};
    },


    startJob: (state, action: PayloadAction<{
      tableId: string;
      enrichmentJobId: string;
      enrichmentDbId: string;
      storeName: string;
      userEmail: string;
      collectionName: string;
      enrichmentPendingChanges: EnrichmentPendingChange[];
      exportFields?: string[];
    }>) => {
      const updateFields = action.payload.enrichmentPendingChanges.filter((el) => el.enrichmentJobId === action.payload.enrichmentJobId)
      .map((change) => ({
        product_id: change.productId,
        field_name: change.fieldName,
        field_value: change.fieldValue,
        ...(change.sourceId && { source_id: change.sourceId }),
      }));
      console.log(action.payload.exportFields )
      
      state.activeExportJobs[action.payload.enrichmentJobId] = {
        enrichmentJobId: action.payload.enrichmentJobId,
        enrichmentDbId: action.payload.enrichmentDbId,
        collectionName: action.payload.collectionName,
        userEmail: action.payload.userEmail,
        storeName: action.payload.storeName,
        tableId: action.payload.tableId,
        currentSubTask: 0,
        progress: 0,
        status: "running",
        startedAt: Date.now(),
        ...(updateFields.length > 0 && { updatedFields: updateFields }),
        ...(action.payload.exportFields && { exportedFields: action.payload.exportFields }),
      };
    },
    addUpdateJobId : (state, action: PayloadAction<{enrichmentJobId: string; updateJobId: string}>) => {
      const job = state.activeExportJobs[action.payload.enrichmentJobId];
      if (job) {
        job.updateJobId = action.payload.updateJobId;
      }
    },
    addExportJobId : (state, action: PayloadAction<{enrichmentJobId: string; exportJobId: string}>) => {
      const job = state.activeExportJobs[action.payload.enrichmentJobId];
      if (job) {
        job.exportJobId = action.payload.exportJobId;
      }
    },
    addSyncJobId : (state, action: PayloadAction<{enrichmentJobId: string; syncJobId: string}>) => {
      const job = state.activeExportJobs[action.payload.enrichmentJobId];
      if (job) {
        job.syncJobId = action.payload.syncJobId;
      }
    },
    updateJobProgress: (state, action: PayloadAction<{ jobId: string; progress: number | undefined}>) => {
      const job = state.activeExportJobs[action.payload.jobId];
      if (job && action.payload.progress !== undefined) {
        job.progress = action.payload.progress;
      }
    },
    moveToNextSubtask: (state, action: PayloadAction<string>) => {
      const job = state.activeExportJobs[action.payload];
      if (job) {
        job.currentSubTask++;
      }
    },
    completeJob: (state, action: PayloadAction<string>) => {
      const job = state.activeExportJobs[action.payload];
      if (job) {
        job.status = "completed";
      }
      delete state.activeExportJobs[action.payload];
    },
    failJob: (state, action: PayloadAction<{
      jobId: string;
      reason: string;
      timestamp?: number;
    }>) => {
      const job = state.activeExportJobs[action.payload.jobId];
      if (job) {
        job.status = "failed";
        job.error = {
          failReason: action.payload.reason,
          failTimestamp: action.payload.timestamp || Date.now(),
        }
      }
    },
    resetExportJob: (state, action: PayloadAction<string>) => {
      delete state.activeExportJobs[action.payload];
    }
  },
});

export const selectActiveJobs = (state: RootState) => state.jobs.activeExportJobs;
export const selectActiveGenericJobs = (state: RootState) => Object.values(state.jobs.activeGenericJobs).filter(job => job.status === 'idle' || job.status === 'running');
export const { startJob, updateJobProgress, moveToNextSubtask, completeJob, failJob, resetExportJob } = jobsSlice.actions;
export const {addUpdateJobId, addExportJobId, addSyncJobId} = jobsSlice.actions;
export const { startGenericJob, deleteGenericJob, completeGenericJob, failGenericJob, updateGenericJobProgress } = jobsSlice.actions;
export default jobsSlice.reducer;
